import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import BadgeStar from '../../components/shared/BadgeStar'

const CATEGORIES = ['Semua', 'Konsistensi', 'Performa', 'Milestone', 'Sesi', 'Hidden']

export default function AchievementPage({ profile, athleteId, onBack }) {
  const [activeCategory, setActiveCategory] = useState('Semua')
  const [selectedBadge, setSelectedBadge] = useState(null)
  const [angle, setAngle] = useState(0)
  const [allBadges, setAllBadges] = useState([])
  const [earnedIds, setEarnedIds] = useState({})
  const [earnedDates, setEarnedDates] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchBadges()
  }, [athleteId])

  useEffect(() => {
    const interval = setInterval(() => {
      setAngle(a => (a + 0.3) % 360)
    }, 16)
    return () => clearInterval(interval)
  }, [])

  const fetchBadges = async () => {
    setLoading(true)

    // Ambil semua badge
    const { data: badges } = await supabase
      .from('badges')
      .select('*')
      .order('category')

    // Ambil badge yang sudah diraih atlet
    const { data: earned } = await supabase
      .from('athlete_badges')
      .select('badge_id, earned_at')
      .eq('athlete_id', athleteId)

    const earnedMap = {}
    const dateMap = {}
    earned?.forEach(e => {
      earnedMap[e.badge_id] = true
      dateMap[e.badge_id] = e.earned_at?.slice(0, 10)
    })

    setAllBadges(badges || [])
    setEarnedIds(earnedMap)
    setEarnedDates(dateMap)
    setLoading(false)
  }

  const earned = allBadges.filter(b => earnedIds[b.id])

  const filtered = activeCategory === 'Semua'
    ? allBadges
    : allBadges.filter(b =>
        b.category?.toLowerCase() === activeCategory.toLowerCase()
      )

  const tooltipStyle = {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '0.75rem'
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0f1e',
      color: '#f1f5f9',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background stars */}
      {[...Array(30)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: i % 3 === 0 ? '3px' : '2px',
          height: i % 3 === 0 ? '3px' : '2px',
          background: '#ffffff',
          borderRadius: '50%',
          top: `${Math.sin(i * 137.5) * 50 + 50}%`,
          left: `${Math.cos(i * 137.5) * 50 + 50}%`,
          opacity: 0.2 + (i % 5) * 0.1,
        }} />
      ))}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #1e293b',
        background: '#0f172a',
        position: 'relative', zIndex: 10
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: '1px solid #334155',
          color: '#94a3b8', padding: '0.4rem 0.75rem',
          borderRadius: '8px', cursor: 'pointer',
          fontSize: '0.85rem', marginRight: '1rem'
        }}>
          ← Kembali
        </button>
        <div>
          <h2 style={{ margin: 0, color: '#facc15', fontSize: '1.2rem' }}>
            🏆 Achievement
          </h2>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.75rem' }}>
            {loading ? '...' : `${earned.length} dari ${allBadges.length} badge diraih`}
          </p>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 10, padding: '1.5rem' }}>

        {/* Bintang besar berputar */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <div style={{ transform: `rotate(${angle}deg)`, transition: 'transform 0.016s linear' }}>
            <BadgeStar size={100} color='#facc15' earned={true} icon='🏆' name='' />
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex', justifyContent: 'center',
          gap: '2rem', marginBottom: '1.5rem'
        }}>
          {[
            { label: 'Diraih', value: earned.length, color: '#facc15' },
            { label: 'Total', value: allBadges.length, color: '#94a3b8' },
            { label: 'Hidden', value: allBadges.filter(b => b.is_hidden).length, color: '#a78bfa' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: s.color }}>
                {loading ? '...' : s.value}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filter kategori */}
        <div style={{
          display: 'flex', gap: '0.5rem',
          flexWrap: 'wrap', justifyContent: 'center',
          marginBottom: '1.5rem'
        }}>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} style={{
              padding: '0.35rem 0.85rem', borderRadius: '999px',
              border: activeCategory === cat ? '2px solid #facc15' : '2px solid #334155',
              background: activeCategory === cat ? '#facc1520' : 'transparent',
              color: activeCategory === cat ? '#facc15' : '#94a3b8',
              cursor: 'pointer', fontSize: '0.8rem',
              fontWeight: activeCategory === cat ? 'bold' : 'normal'
            }}>
              {cat}
            </button>
          ))}
        </div>

        {/* Badge grid */}
        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
            Loading badge...
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            gap: '1rem', justifyItems: 'center'
          }}>
            {filtered.map(badge => (
              <div
                key={badge.id}
                onClick={() => setSelectedBadge(badge)}
                style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <BadgeStar
                  size={50}
                  color={badge.color || '#facc15'}
                  earned={!!earnedIds[badge.id]}
                  icon={badge.is_hidden && !earnedIds[badge.id] ? '?' : (badge.icon || '⭐')}
                  name={badge.is_hidden && !earnedIds[badge.id] ? '???' : badge.name}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal detail badge */}
      {selectedBadge && (
        <div
          onClick={() => setSelectedBadge(null)}
          style={{
            position: 'fixed', inset: 0, background: '#00000088',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 100, padding: '1rem'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#1e293b',
              border: `2px solid ${earnedIds[selectedBadge.id] ? selectedBadge.color : '#334155'}`,
              borderRadius: '16px', padding: '2rem',
              maxWidth: '300px', width: '100%',
              textAlign: 'center',
              boxShadow: earnedIds[selectedBadge.id]
                ? `0 0 30px ${selectedBadge.color}44` : 'none'
            }}
          >
            <BadgeStar
              size={80}
              color={selectedBadge.color || '#facc15'}
              earned={!!earnedIds[selectedBadge.id]}
              icon={selectedBadge.is_hidden && !earnedIds[selectedBadge.id] ? '?' : selectedBadge.icon}
              name=''
            />
            <h3 style={{
              color: earnedIds[selectedBadge.id] ? selectedBadge.color : '#94a3b8',
              margin: '1rem 0 0.5rem'
            }}>
              {selectedBadge.is_hidden && !earnedIds[selectedBadge.id]
                ? '???'
                : selectedBadge.name}
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 1rem' }}>
              {selectedBadge.is_hidden && !earnedIds[selectedBadge.id]
                ? 'Selesaikan kondisi tersembunyi untuk membuka badge ini'
                : selectedBadge.description}
            </p>
            {earnedIds[selectedBadge.id] && (
              <div style={{
                background: `${selectedBadge.color}22`,
                border: `1px solid ${selectedBadge.color}44`,
                borderRadius: '8px', padding: '0.5rem',
                color: selectedBadge.color, fontSize: '0.8rem'
              }}>
                🎉 Diraih pada {earnedDates[selectedBadge.id] || '-'}
              </div>
            )}
            {!earnedIds[selectedBadge.id] && !selectedBadge.is_hidden && (
              <div style={{
                background: '#0f172a', borderRadius: '8px',
                padding: '0.5rem', color: '#475569', fontSize: '0.8rem'
              }}>
                🔒 Belum diraih
              </div>
            )}
            <button
              onClick={() => setSelectedBadge(null)}
              style={{
                marginTop: '1rem', padding: '0.5rem 1.5rem',
                background: '#334155', border: 'none',
                borderRadius: '8px', color: '#94a3b8',
                cursor: 'pointer'
              }}
            >
              Tutup
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes twinkle {
          from { opacity: 0.2; }
          to { opacity: 0.8; }
        }
      `}</style>
    </div>
  )
}

